import { isNil } from 'lodash'
import { pixel2Time, time2Pixel } from '~/Util'
import { checkIntersectionStaticAssets } from './checkIntersectionStaticAssets'
import { getPlaceHolderIntersection } from './getPlaceholderIntersection'
import { getMinLeftNMinTopDraggableCoords } from './getMinLeftNMinTopDraggableCoords'
import { setInitialHistoryX } from './setInitialHistoryX'
import { getMoveDirection } from './getMoveDirection'
import { setDraggableBlockLeftX } from './setDraggableBlockLeftX'
import { setDropX } from './setDropX'
import { setIntersectedXParams } from './setIntersectedXParams'
import { checkIsNeedResetXIntersectionParams } from './checkIsNeedResetXIntersectionParams'
import { setQuickOffset } from './setQuickOffset'
import { checkStickySlider } from './checkStickySlider'

/**
 * clientOffset - mouse coords (x, y). Current changing values.
 * initialClientOffset - initial (start) mouse coords (x, y). Сonstant unchanged values.
 * sourceOffset - current draggable asset (single asset) coords (x, y). Current changing values.
 * historyX - history of asset group movements
 * dragAssetsWidth - width of the dragged block in pixels
 * draggableBlockDuration - width of the dragged block in nanosecond units
 * isExternalSource - timeline assets or source media files
 * targetLayerIndex - index of the layer that the mouse cursor is pointing to
 * overNewLayerIndex - the cursor points to a new layer
 */

export const useMultipleDragRectCoords = ({
  dragItem,
  clientOffset,
  initialClientOffset,
  timelineTopOffset,
  sourceOffset,
  leftXOffset,
  setDraggingItemProps,
  historyX,
  staticAssets,
  scale,
  draggableAssets,
  overNewLayerIndex,
  layers,
  layerIds,
  targetLayerIndex,
  placeholdersByLayerIndex,
  diffBetweenCurNTopDragLayers,
  isMultipleDrag,
  transitions,
  sliderTimeX,
  dragAssetsWidth,
  setSliderSticky,
  timelineMouseDown,
  isExternalSource,
  draggableBlockDuration,
}) => {
  if (!isMultipleDrag) return {}
  // Left startX coordinates of the leftmost asset
  const {
    minLeftX,
    topY,
  } = getMinLeftNMinTopDraggableCoords(dragItem, timelineTopOffset, sourceOffset,
    diffBetweenCurNTopDragLayers, scale, leftXOffset, isExternalSource)

  // start layerIndex of single draggable asset
  const initialLayerIndex = isExternalSource ? 0 : dragItem.layerIndex

  // offset relative to starting layer (relative to initialLayerIndex)
  const offsetLayerIndex = targetLayerIndex - initialLayerIndex

  // Left X coordinate of the dragged block of assets
  const draggableBlockLeftX = isExternalSource
    ? clientOffset.x
    : clientOffset.x - (initialClientOffset.x - minLeftX)

  // Offset of the dragged block relative to the current position. May be less than zero.
  const shiftingDragAssets = isExternalSource
    ? pixel2Time(sourceOffset.x - leftXOffset, scale)
    : pixel2Time(sourceOffset.x - leftXOffset, scale) - dragItem.startTime

  // draggableBlockLeftX === time2Pixel(draggableBlockStartTime, scale) + leftXOffset
  const draggableBlockStartTime = isExternalSource
    ? shiftingDragAssets
    : draggableAssets[0]?.startTime + shiftingDragAssets

  const currentDraggableAsset = isExternalSource
    ? draggableAssets[0]
    : draggableAssets.find(asset => asset.id === dragItem.id)

  const diffBetweenCurrentDragItemNFirstDragItem = isExternalSource
    ? 0
    : dragItem.startTime - (draggableAssets[0]?.startTime ?? 0)

  // First draggable elem transition
  const firstDraggableFadeIn = transitions
    .find(tr => tr.isAttachedToRight(draggableAssets?.[0].id) && tr.isMountIn)
  // If the first dragged element is attached to the FadeIn transition, add an offset for draggableBlock
  const transitionOffset = time2Pixel(firstDraggableFadeIn?.duration ?? 0, scale)
  // Current 'x' mouse position on the timeline in nanosecond units
  const clientMousePosition = pixel2Time(clientOffset.x - leftXOffset, scale)

  setInitialHistoryX({
    historyX,
    draggableBlockLeftX,
    sourceOffset,
    leftXOffset,
    targetLayerIndex,
    overNewLayerIndex,
  })

  // Mouse movement direction
  const currentMoveDirection = getMoveDirection(historyX, draggableBlockLeftX)

  const {
    intersectionPlaceholder,
    intersectionEqualSegment,
    insertBetween,
    deltaOnDrop,
  } = getPlaceHolderIntersection({
    placeholdersByLayerIndex,
    dragItem,
    layerIds,
    offsetLayerIndex,
    scale,
    draggableBlockStartTime,
    draggableAssets,
    overNewLayerIndex,
    isExternalSource,
    draggableBlockDuration,
    clientMousePosition,
    historyX,
  })

  const {
    isOverlapped,
    isIntersected,
    isMouseOver,
    intersectedData,
  } = checkIntersectionStaticAssets({
    staticAssets,
    draggableAssets,
    layers,
    shiftingDragAssets,
    offsetLayerIndex,
    isExternalSource,
    draggableBlockDuration,
    clientMousePosition,
    dragItem,
  })

  // Set historyX stopDragLeftX & intersectedData params
  setIntersectedXParams({
    staticAssets,
    draggableAssets,
    layers,
    offsetLayerIndex,
    dragItem,
    clientMousePosition,
    historyX,
    moveDirection: currentMoveDirection,
    leftXOffset,
    scale,
    isIntersected,
    intersectedData,
    draggableBlockLeftX,
    draggableBlockDuration,
    isExternalSource,
    isPlaceholder: insertBetween || intersectionPlaceholder,
  })

  // Sets quickOffset and applies it. To quickly move dragged assets when assets overlap (isMouseOver)
  setQuickOffset({
    draggableAssets,
    historyX,
    moveDirection: currentMoveDirection,
    scale,
    staticAssets,
    layers,
    clientMousePosition,
    isExternalSource,
    draggableBlockDuration,
    shiftingDragAssets,
    isMouseOver,
    dragItem,
    initialClientOffset,
    leftXOffset,
    intersectionPlaceholder,
    insertBetween,
    offsetLayerIndex,
    isOverlapped,
  })

  checkIsNeedResetXIntersectionParams({
    isOverlapped,
    isIntersected,
    isMouseOver,
    historyX,
    moveDirection: currentMoveDirection,
    targetLayerIndex,
  })

  // Sets leftX coord for draggable assets block
  let {
    finalDraggableBlockLeftX,
    dropOffsetX,
  } = setDraggableBlockLeftX({
    dragItem,
    historyX,
    draggableBlockStartTime,
    leftXOffset,
    intersectionPlaceholder,
    insertBetween,
    scale,
    draggableBlockLeftX,
    initialLayerIndex,
    isOverlapped,
    overNewLayerIndex,
    transitionOffset,
    isExternalSource,
  })

  // Sets drop X coord for current dragItem. Other assets are aligned relative to dragItem.
  let {
    dropX,
  } = setDropX({
    historyX,
    draggableBlockStartTime,
    leftXOffset,
    currentDraggableAsset,
    draggableAssets,
    intersectionPlaceholder,
    insertBetween,
    scale,
    sourceOffset,
    isIntersected,
    isOverlapped,
    overNewLayerIndex,
    intersectionEqualSegment,
    diffBetweenCurrentDragItemNFirstDragItem,
    draggableBlockDuration,
    isExternalSource,
  })

  const {
    isSliderLeftSticky,
    isSliderRightSticky,
    rightOffset,
    leftOffset,
  } = checkStickySlider({
    sourceX: finalDraggableBlockLeftX,
    dragAssetsWidth,
    sliderTimeX,
  })

  if (timelineMouseDown.current
    && !isIntersected
    && !isOverlapped
    && !intersectionPlaceholder
    && !insertBetween
    && !intersectionEqualSegment) {
    if (isSliderLeftSticky && !isSliderRightSticky) {
      // place a group of assets on the slider position
      finalDraggableBlockLeftX = sliderTimeX
      // aligning the clip insertion
      dropX += leftOffset
      window.requestAnimationFrame(() => setSliderSticky(true))
    } else if (isSliderRightSticky) {
      // place a group of assets on the slider position
      finalDraggableBlockLeftX = sliderTimeX - dragAssetsWidth
      // aligning the clip insertion
      dropX -= rightOffset
      window.requestAnimationFrame(() => setSliderSticky(true))
    } else {
      window.requestAnimationFrame(() => setSliderSticky(false))
    }
  }

  // eslint-disable-next-line no-param-reassign
  historyX.current = {
    ...historyX.current,
    draggableBlockLeftX: finalDraggableBlockLeftX,
    dropX,
  }

  setDraggingItemProps({
    normalizedSourceX: dropX,
    deltaOnDrop,
    dropOffsetX,
    draggableBlockStartTime,
    intersectionPlaceholder,
    insertBetween,
    isExternalSource,
    isIntersectionPlaceholder: Boolean(intersectionPlaceholder || intersectionEqualSegment),
    isInsertBetween: Boolean(insertBetween),
    disableDrop: isNil(overNewLayerIndex) && historyX.current.isIncorrectPosition
    && !(insertBetween || intersectionPlaceholder),
  })

  return {
    draggableBlockLeftX: historyX.current.draggableBlockLeftX,
    draggableBlockY: topY,
    intersectionPlaceholder: insertBetween || intersectionPlaceholder,
    isIncorrectPosition: historyX.current.isIncorrectPosition,
  }
}
